import { RefObject, useLayoutEffect, useState } from 'react';
import { usePropsAsState } from './usePropAsState';

type ScrollDirection = 'vertical' | 'horizontal';

/**
 * Hook that uses a scroll listener and resize observer to detect if a scrolling
 * container has been fully scrolled to the end of its content.
 */
export const useScrolledToEnd = (
  elementRef: RefObject<HTMLElement | null>,
  direction: ScrollDirection = 'vertical',
  threshold = 10
): boolean => {
  const [isAtEnd, setIsAtEnd] = useState(false);
  const [element] = usePropsAsState(elementRef.current);

  useLayoutEffect(() => {
    if (!element) return;

    const checkIfAtEnd = () => {
      if (!element) return;

      const isVertical = direction === 'vertical';
      const scrollDimension = isVertical ? element.scrollHeight : element.scrollWidth;
      const clientDimension = isVertical ? element.clientHeight : element.clientWidth;
      const scrollPosition = isVertical ? element.scrollTop : element.scrollLeft;

      // If content doesn't overflow, consider it at the end
      if (scrollDimension <= clientDimension) {
        setIsAtEnd(true);
        return;
      }

      // Check remaining scroll is less than threshold
      const diff = Math.abs(scrollDimension - clientDimension - scrollPosition);
      setIsAtEnd(diff < threshold);
    };

    checkIfAtEnd();

    element.addEventListener('scroll', checkIfAtEnd, { passive: true });
    const resizeObserver = new ResizeObserver(() => checkIfAtEnd());
    resizeObserver.observe(element);

    return () => {
      if (element) {
        element.removeEventListener('scroll', checkIfAtEnd);
        resizeObserver.disconnect();
      }
    };
  }, [element, direction, threshold]);

  return isAtEnd;
};
